'use client';

import React, { useEffect, useState } from 'react';
import { m } from 'motion/react';
import { css, cx } from 'styled-system/css';

import type { Heading } from '@shaojiang/components/cms/headings';
import { useDesignMotion } from '@shaojiang/components/motion';

const link = css({
  position: 'relative',
  isolation: 'isolate',
  display: 'block',
  px: '3',
  py: '1.5',
  textStyle: 'xs',
  fontWeight: '700',
  color: 'fg.muted',
  borderRadius: 'l2',
  transition: 'color {durations.base}',
  _hover: { color: 'fg.default' },
  '&[aria-current=true]': { color: 'fg.default' },
  _nebula: { fontWeight: '500' },
});

const marker = css({
  position: 'absolute',
  zIndex: '-1',
  left: '0',
  top: '20%',
  bottom: '20%',
  w: '1',
  borderRadius: 'full',
  bg: 'hot.default',
  _nebula: { inset: '0', w: 'auto', borderRadius: 'l2', bg: 'brand.subtle' },
});

/** "On this page": follows the reading position; the marker slides to the current heading. */
export function ArticleToc({ headings, label }: { headings: Heading[]; label: React.ReactNode }) {
  const [current, setCurrent] = useState(headings[0]?.id);
  const motion = useDesignMotion();

  useEffect(() => {
    const elements = headings
      .map((h) => document.getElementById(h.id))
      .filter((el): el is HTMLElement => !!el);
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible[0]) setCurrent(visible[0].target.id);
      },
      { rootMargin: '-15% 0px -70% 0px' },
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [headings]);

  if (headings.length === 0) return null;

  return (
    <nav aria-label={typeof label === 'string' ? label : undefined}>
      <p className={css({ textStyle: 'label', color: 'fg.subtle', px: '3', pb: '2' })}>{label}</p>
      {headings.map((heading) => {
        const active = heading.id === current;
        return (
          <a
            key={heading.id}
            href={`#${heading.id}`}
            aria-current={active}
            className={cx(link, heading.depth === 3 && css({ pl: '6' }))}
          >
            {active && (
              <m.span layoutId='toc-marker' className={marker} transition={motion.layout} />
            )}
            {heading.text}
          </a>
        );
      })}
    </nav>
  );
}
